import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  forwardRef,
  ElementRef,
  ViewChild,
  signal,
  computed,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideIconsModule } from '../../modules/lucide-icons.module';

@Component({
  selector: 'app-icons-dropdown',
  templateUrl: './icons-dropdown.component.html',
  styleUrls: ['./icons-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IconsDropdownComponent),
      multi: true,
    },
  ],
})
export class IconsDropdownComponent implements ControlValueAccessor, OnDestroy {
  @ViewChild('dropdownButton') dropdownButton!: ElementRef;
  @ViewChild('dropdownPanel') dropdownPanel!: ElementRef;

  searchTerm = signal('');
  selectedIcon: string = 'home';
  isOpen = false;
  onChange: (icon: string) => void = () => { };
  onTouched: () => void = () => { };

  private scrollableParent: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;

  constructor(private elementRef: ElementRef) { }


  icons: { name: string; }[] = [

    { name: 'home' },
    { name: 'menu' },
    { name: 'layout-dashboard' },
    { name: 'layout-grid' },
    { name: 'panels-top-left' },
    { name: 'panel-top-open' },
    { name: 'panel-top-close' },
    { name: 'rectangle-ellipsis' },
    { name: 'compass' },
    { name: 'navigation' },

    { name: 'file-text' },
    { name: 'file-clock' },
    { name: 'file-spreadsheet' },
    { name: 'file-check' },
    { name: 'file-x' },
    { name: 'file-search' },
    { name: 'file-box' },
    { name: 'file-question' },
    { name: 'book-text' },
    { name: 'book-dashed' },
    { name: 'book-marked' },
    { name: 'library' },
    { name: 'folder' },
    { name: 'folder-open' },
    { name: 'folder-cog' },
    { name: 'folder-kanban' },
    { name: 'notebook-tabs' },
    { name: 'inbox' },
    { name: 'square-kanban' },
    { name: 'folder-plus' },
    { name: 'file-plus' },
    { name: 'clipboard-list' },
    { name: 'clipboard-check' },
    { name: 'archive' },
    { name: 'package' },
    // Charts / data
    { name: 'bar-chart-3' },
    { name: 'pie-chart' },
    { name: 'line-chart' },
    { name: 'area-chart' },
    { name: 'trending-up' },
    { name: 'activity' },
    { name: 'circle-gauge' },
    { name: 'gauge' },
    { name: 'target' },
    { name: 'sliders-horizontal' },

    { name: 'hand-coins' },
    { name: 'circle-dollar-sign' },
    { name: 'landmark' },
    { name: 'building-2' },
    { name: 'briefcase' },
    { name: 'wallet' },
    { name: 'credit-card' },
    { name: 'banknote' },
    { name: 'scale' },
    { name: 'calendar' },
    { name: 'calendar-clock' },
    { name: 'clock' },
    { name: 'timer' },
    { name: 'history' },

    { name: 'user' },
    { name: 'user-check' },
    { name: 'user-plus' },
    { name: 'user-cog' },
    { name: 'users' },
    { name: 'eye' },

    { name: 'search' },
    { name: 'filter' },
    { name: 'settings' },
    { name: 'wrench' },
    { name: 'hammer' },
    { name: 'plus' },
    { name: 'edit' },
    { name: 'edit-3' },
    { name: 'pencil' },
    { name: 'trash-2' },
    { name: 'undo' },
    { name: 'undo-2' },
    { name: 'rotate-ccw' },
    { name: 'refresh-cw' },
    { name: 'save' },
    { name: 'replace' },
    { name: 'x' },
    { name: 'check' },
    { name: 'loader' },
    { name: 'loader-2' },
    { name: 'fullscreen' },
    { name: 'maximize-2' },
    { name: 'minimize-2' },
    { name: 'fold-vertical' },
    { name: 'unfold-vertical' },
    { name: 'minus-square' },
    { name: 'plus-square' },
    { name: 'zoom-in' },
    { name: 'zoom-out' },
    { name: 'list-plus' },
    { name: 'list' },
    { name: 'list-x' },
    { name: 'combine' },
    { name: 'git-pull-request-draft' },
    { name: 'git-pull-request-create-arrow' },

    { name: 'bell' },
    { name: 'info' },
    { name: 'help-circle' },
    { name: 'newspaper' },
    { name: 'megaphone' },
    { name: 'sticky-note' },
    { name: 'external-link' },
    { name: 'link' },
    { name: 'message-circle-question' },
    { name: 'mail' },
    { name: 'send' },
    { name: 'message-square' },
    { name: 'message-circle' },

    { name: 'alert-triangle' },
    { name: 'alert-circle' },
    { name: 'check-circle' },
    { name: 'check-circle-2' },
    { name: 'x-circle' },
    { name: 'circle-x' },
    { name: 'circle' },
    { name: 'shield' },
    { name: 'lock' },
    { name: 'lock-open' },

    { name: 'map' },
    { name: 'pin' },
    { name: 'map-pin' },
    { name: 'map-pinned' },

    { name: 'camera' },
    { name: 'video' },
    { name: 'headphones' },
    { name: 'mic' },
    { name: 'image' },

    { name: 'book-open' },
    { name: 'graduation-cap' },

    { name: 'heart' },
    { name: 'bookmark' },
    { name: 'sparkles' },
    { name: 'zap' },

    { name: 'layers' },
    { name: 'table' },
    { name: 'presentation' },

    { name: 'star' },
    { name: 'flag' },
    { name: 'bug' },
    { name: 'award' },
    { name: 'gavel' },
    { name: 'eraser' },
    { name: 'database-backup' },
    { name: 'database' },
    { name: 'boxes' },
    { name: 'server-crash' },
    { name: 'log-out' },
  ];

  filteredIcons = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) return this.icons;
    return this.icons.filter((icon) => icon.name.toLowerCase().includes(term));
  });

  @HostListener('document:click', ['$event.target'])
  public onClick(target: EventTarget | null): void {
    const el = target instanceof HTMLElement ? target : null;
    if (!el) return;
    const clickedInside = this.elementRef.nativeElement.contains(el);
    if (!clickedInside) {
      this.isOpen = false;
      this.detachScrollListener();
    }
  }

  writeValue(obj: unknown): void {
    this.selectedIcon = (obj as string) || 'home';
  }

  registerOnChange(fn: (icon: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm.set('');
      setTimeout(() => {
        this.updateDropdownPosition();
        this.attachScrollListener();
      });
    } else {
      this.detachScrollListener();
    }
  }

  private updateDropdownPosition(): void {
    if (!this.dropdownButton?.nativeElement || !this.dropdownPanel?.nativeElement)
      return;
    const button = this.dropdownButton.nativeElement as HTMLElement;
    const panel = this.dropdownPanel.nativeElement as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    const gap = 8;

    panel.style.position = 'fixed';

    const spaceBelow = window.innerHeight - buttonRect.bottom - gap;
    if (spaceBelow >= Math.min(panel.offsetHeight || 320, 280)) {
      panel.style.top = `${buttonRect.bottom + gap}px`;
      panel.style.bottom = 'auto';
    } else {
      panel.style.top = 'auto';
      panel.style.bottom = `${window.innerHeight - buttonRect.top + gap}px`;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) this.updateDropdownPosition();
  }

  onSearchInput(value: string): void {
    this.searchTerm.set(value);
  }

  selectIcon(icon: string): void {
    this.selectedIcon = icon;
    this.onChange(icon);
    this.isOpen = false;
    this.detachScrollListener();
  }

  ngOnDestroy(): void {
    this.detachScrollListener();
  }

  private findScrollableParent(el: HTMLElement): HTMLElement | null {
    let current = el.parentElement;
    while (current) {
      const { overflow, overflowY } = window.getComputedStyle(current);
      if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  private attachScrollListener(): void {
    if (!this.dropdownButton?.nativeElement) return;
    this.scrollableParent = this.findScrollableParent(
      this.dropdownButton.nativeElement as HTMLElement
    );
    if (this.scrollableParent) {
      this.scrollListener = () => this.updateDropdownPosition();
      this.scrollableParent.addEventListener('scroll', this.scrollListener);
    }
  }

  private detachScrollListener(): void {
    if (this.scrollableParent && this.scrollListener) {
      this.scrollableParent.removeEventListener('scroll', this.scrollListener);
    }
    this.scrollableParent = null;
    this.scrollListener = null;
  }
}
